<template>
  <div class="instant-checkout-page">
    <LoadingState
      v-if="isLoadingCart"
      message="Loading checkout..."
      min-height="60vh"
    />

    <template v-else>
      <!-- Progress -->
      <div class="checkout-progress">
        <router-link
          :to="{ name: 'MakerCartCartPage' }"
          class="checkout-progress__section checkout-progress__section--link"
        >
          <div class="checkout-progress__icon checkout-progress__icon--done">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M16.667 5L7.5 14.167 3.333 10"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <span
            class="checkout-progress__label checkout-progress__label--active"
          >
            Review Cart
          </span>
        </router-link>

        <div
          class="checkout-progress__line checkout-progress__line--active"
        ></div>

        <div class="checkout-progress__section">
          <div
            class="checkout-progress__icon"
            :class="
              isPaymentSuccessful
                ? 'checkout-progress__icon--done'
                : 'checkout-progress__icon--current'
            "
          >
            <svg
              v-if="isPaymentSuccessful"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M16.667 5L7.5 14.167 3.333 10"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span v-else class="checkout-progress__dot"></span>
          </div>
          <span
            class="checkout-progress__label checkout-progress__label--active"
          >
            Instant Checkout
          </span>
        </div>

        <div
          class="checkout-progress__line"
          :class="{ 'checkout-progress__line--active': isPaymentSuccessful }"
        ></div>

        <div class="checkout-progress__section">
          <div
            class="checkout-progress__icon"
            :class="
              isPaymentSuccessful
                ? 'checkout-progress__icon--done'
                : 'checkout-progress__icon--pending'
            "
          >
            <svg
              v-if="isPaymentSuccessful"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M16.667 5L7.5 14.167 3.333 10"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span
              v-else
              class="checkout-progress__dot checkout-progress__dot--pending"
            ></span>
          </div>
          <span
            class="checkout-progress__label"
            :class="{ 'checkout-progress__label--active': isPaymentSuccessful }"
          >
            Successful
          </span>
        </div>
      </div>

      <div class="checkout-divider"></div>

      <!-- Payment success -->
      <div v-if="isPaymentSuccessful" class="payment-success">
        <div class="payment-success__hero">
          <img
            src="/images/maker-cart/ok-bro.svg"
            alt=""
            aria-hidden="true"
            class="payment-success__illustration"
            width="500"
            height="500"
          />
          <div class="payment-success__heading">
            <h1 class="payment-success__title">Payment Successful!</h1>
            <p class="payment-success__subtitle">Thank you for your order.</p>
          </div>
        </div>

        <div class="payment-success__next">
          <h2 class="payment-success__next-title">What's next?</h2>
          <div class="payment-success__next-divider"></div>
          <div class="payment-success__next-body">
            <p>
              Our Ops team has been notified and physical and fulfilment of your
              order has begun. You'll receive a shipping confirmation with
              tracking details shortly.
            </p>
            <a
              class="payment-success__link"
              href="/makercart/catalog?tab=makercrate"
              @click.prevent="goToMakercrateResources"
            >
              Access your digital resources for the Makercrate on Eddy.
            </a>
          </div>
          <ActionButton variant="primary" full-width @click="goToMakerCatalog">
            Return to Maker Products catalog
          </ActionButton>
        </div>
      </div>

      <template v-else>
        <button type="button" class="checkout-back" @click="handleBack">
          <img
            src="/images/icon/arrow-circle-left.svg"
            alt=""
            aria-hidden="true"
            class="checkout-back__icon"
          />
          <span>Back</span>
        </button>

        <div class="checkout-content">
          <!-- Left panel -->
          <div class="checkout-main">
            <div class="checkout-payment">
              <div class="checkout-main__header">
                <h1 class="checkout-main__title">Instant Checkout</h1>
                <p class="checkout-main__subtitle">
                  Fast, secure payment for immediate fulfillment.
                </p>
              </div>
              <div class="checkout-main__divider"></div>

              <StripeCheckoutForm
                ref="stripeFormRef"
                :amount="grandTotal"
                currency="usd"
                :prefill="stripePrefill"
                :client-secret="clientSecret"
                :shipping-rates="stripeExpressShippingRates"
                :prepare-payment="preparePaymentFromStripe"
                :prepare-shipping-rates="prepareShippingRatesFromStripe"
                @payment-success="handlePaymentSuccess"
                @shipping-change="handleStripeShippingChange"
              />

              <label class="residential-check">
                <span class="residential-check__control">
                  <input
                    v-model="isResidential"
                    type="checkbox"
                    class="residential-check__input"
                  />
                  <span
                    class="residential-check__box"
                    aria-hidden="true"
                  ></span>
                </span>
                <span class="residential-check__copy">
                  <span class="residential-check__title"
                    >Residential address</span
                  >
                  <span class="residential-check__subtitle">
                    Check this if you're shipping to a home instead of business
                  </span>
                </span>
              </label>

              <div
                class="shipping-options"
                :class="{ 'shipping-options--loading': isLoadingRates }"
                :aria-busy="isLoadingRates"
              >
                <div class="shipping-options__heading">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M16 3H1V16H16V3Z"
                      stroke="#3B4648"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M16 8H20L23 11V16H16V8Z"
                      stroke="#3B4648"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <circle
                      cx="5.5"
                      cy="18.5"
                      r="2.5"
                      stroke="#3B4648"
                      stroke-width="1.5"
                    />
                    <circle
                      cx="18.5"
                      cy="18.5"
                      r="2.5"
                      stroke="#3B4648"
                      stroke-width="1.5"
                    />
                  </svg>
                  <span>FedEx Delivery Options</span>
                </div>

                <div
                  v-if="isLoadingRates"
                  class="shipping-options__skeletons"
                  aria-hidden="true"
                >
                  <div
                    v-for="n in 3"
                    :key="`shipping-skeleton-${n}`"
                    class="shipping-option shipping-option--skeleton"
                  >
                    <span
                      class="shipping-option__radio shipping-skeleton__radio"
                    ></span>
                    <div class="shipping-option__body">
                      <div class="shipping-option__info">
                        <span
                          class="shipping-skeleton__line shipping-skeleton__line--title"
                        ></span>
                        <span
                          class="shipping-skeleton__line shipping-skeleton__line--meta"
                        ></span>
                        <span
                          class="shipping-skeleton__line shipping-skeleton__line--eta"
                        ></span>
                      </div>
                      <span
                        class="shipping-skeleton__line shipping-skeleton__line--price"
                      ></span>
                    </div>
                  </div>
                </div>

                <template v-else-if="shippingOptions.length">
                  <button
                    v-for="option in shippingOptions"
                    :key="option.id"
                    type="button"
                    class="shipping-option"
                    :class="{ selected: selectedShippingId === option.id }"
                    @click="selectShippingOption(option.id)"
                  >
                    <span
                      class="shipping-option__radio"
                      :class="{ checked: selectedShippingId === option.id }"
                      aria-hidden="true"
                    >
                      <span v-if="selectedShippingId === option.id"></span>
                    </span>
                    <div class="shipping-option__body">
                      <div class="shipping-option__info">
                        <div class="shipping-option__copy">
                          <p class="shipping-option__name">{{ option.name }}</p>
                          <p class="shipping-option__meta">
                            {{ option.duration }}
                          </p>
                        </div>
                        <p class="shipping-option__eta">
                          Estimated delivery: {{ option.eta }}
                        </p>
                      </div>
                      <p class="shipping-option__price">
                        ${{ formatPrice(option.price) }}
                      </p>
                    </div>
                  </button>
                </template>

                <div
                  v-else-if="zipNotFound || shippingRatesUnavailable"
                  class="zip-not-found"
                  role="status"
                >
                  <img
                    src="/images/maker-cart/zip-not-found-shrug.png"
                    alt=""
                    aria-hidden="true"
                    class="zip-not-found__illustration"
                    width="344"
                    height="344"
                  />
                  <div class="zip-not-found__copy">
                    <p class="zip-not-found__title">
                      {{
                        zipNotFound
                          ? "We couldn't find that ZIP code"
                          : "We're currently unable to load live FedEx shipping rates"
                      }}
                    </p>
                    <p class="zip-not-found__subtitle">
                      {{
                        zipNotFound
                          ? "Please double-check the ZIP in shipping details and try again."
                          : "Please try refreshing the page or check back in a few minutes."
                      }}
                    </p>
                  </div>
                </div>

                <p v-else class="shipping-options__empty">
                  {{
                    canGetRates
                      ? "No FedEx delivery options are available for this ZIP."
                      : "Enter a ZIP code in shipping details to see FedEx delivery options."
                  }}
                </p>
              </div>

              <ActionButton
                variant="primary"
                full-width
                :loading="isValidatingStripe || isPreparingPayment"
                :disabled="!canContinuePayment"
                @click="handleContinuePayment"
              >
                Continue to Payment →
              </ActionButton>
            </div>

            <p class="checkout-powered">Powered by stripe</p>
          </div>

          <!-- Pricing Summary -->
          <aside class="pricing-summary">
            <h2 class="pricing-summary__title">Pricing Summary</h2>
            <div
              class="pricing-summary__divider pricing-summary__divider--dashed"
            ></div>

            <div class="pricing-summary__body">
              <div
                v-for="item in pricingLineItems"
                :key="item.id"
                class="pricing-summary__row"
              >
                <p class="pricing-summary__item-label">
                  <span class="pricing-summary__item-name">{{
                    item.name
                  }}</span>
                  <span class="pricing-summary__item-qty"
                    >(X{{ item.quantity }})</span
                  >
                </p>
                <p class="pricing-summary__value">
                  ${{ formatPrice(item.lineTotal) }}
                </p>
              </div>

              <div class="pricing-summary__divider"></div>

              <div class="pricing-summary__subtotal">
                <div class="pricing-summary__row">
                  <span>Subtotal</span>
                  <span>${{ formatPrice(productSubtotal) }}</span>
                </div>
                <p v-if="!selectedShipping" class="pricing-summary__note">
                  (Shipping and tax will be calculated after you choose a
                  delivery option)
                </p>
              </div>
              <template v-if="selectedShipping">
                <div class="pricing-summary__row">
                  <span>Shipping</span>
                  <span>${{ formatPrice(shippingCost) }}</span>
                </div>
                <div class="pricing-summary__row">
                  <span>Tax</span>
                  <span>${{ formatPrice(taxAmount) }}</span>
                </div>
              </template>

              <div class="pricing-summary__divider"></div>

              <div class="pricing-summary__row pricing-summary__row--total">
                <p class="pricing-summary__total-label">
                  <span>{{ selectedShipping ? "Grand Total" : "Total" }}</span>
                  <span class="pricing-summary__currency">(USD)</span>
                </p>
                <p class="pricing-summary__total-value">
                  ${{ formatPrice(grandTotal) }}
                </p>
              </div>
            </div>
          </aside>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import ActionButton from "@/components/common/ActionButton.vue";
import LoadingState from "@/components/common/LoadingState.vue";
import StripeCheckoutForm from "@/components/maker-cart/checkout/StripeCheckoutForm.vue";
import { useCart } from "@/composeables/useCart";
import fedexService from "@/services/fedexService";
import stripePaymentService, {
  buildCreatePaymentIntentPayload,
  buildUpdatePaymentIntentPayload,
} from "@/services/stripePaymentService";
import { formatPrice } from "@/utils/index.js";

const router = useRouter();
const notify = inject("notify");

const CHECKOUT_PREFILL_KEY = "eddy_makercrate_checkout_prefill";

const {
  makercrates,
  hasMakercrates,
  makercarts,
  signatureMakercarts,
  singleActivities,
  total,
  cartId,
  syncCartWithAPI,
} = useCart();

const step = ref("payment");
const zipCode = ref("");
const stripeCountryCode = ref("US");
const selectedShippingId = ref("");
const shippingOptions = ref([]);
const isResidential = ref(false);
const fetchedZipCode = ref("");
const fetchedResidential = ref(false);
const zipNotFound = ref(false);
const shippingRatesUnavailable = ref(false);
const ratesRequestId = ref(0);
let ratesDebounceTimer = null;
let hasStripeZipEvent = false;
const shippingUpdateId = ref(0);
const stripeFormRef = ref(null);
const isValidatingStripe = ref(false);
const isPreparingPayment = ref(false);
const isLoadingCart = ref(true);
const isLoadingRates = ref(false);
const isPaymentSuccessful = computed(() => step.value === "success");
const clientSecret = ref("");
const paymentIntentId = ref("");
const stripeTaxCents = ref(0);
const checkoutPrefill = ref({
  organization_name: "",
  attention_to: "",
  address: "",
  address_additional: "",
  city: "",
  state: "",
  zip: "",
  country: "United States",
  email: "",
  phone: "",
});

const loadCheckoutPrefill = () => {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_PREFILL_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    checkoutPrefill.value = {
      ...checkoutPrefill.value,
      ...parsed,
    };
    if (parsed.zip && !zipCode.value) {
      zipCode.value = String(parsed.zip);
    }
  } catch (error) {
    console.warn("Failed to load checkout prefill data:", error);
  }
};

const saveCheckoutPrefill = () => {
  try {
    sessionStorage.setItem(
      CHECKOUT_PREFILL_KEY,
      JSON.stringify(checkoutPrefill.value)
    );
  } catch (error) {
    console.warn("Failed to persist checkout prefill data:", error);
  }
};

const persistStripeDraft = async () => {
  if (!stripeFormRef.value?.getDraftValues) {
    return null;
  }

  try {
    const draft = await stripeFormRef.value.getDraftValues();
    checkoutPrefill.value = {
      ...checkoutPrefill.value,
      attention_to: draft.customerName || checkoutPrefill.value.attention_to,
      address: draft.shippingAddress || checkoutPrefill.value.address,
      address_additional:
        draft.shippingAddressAdditional ||
        checkoutPrefill.value.address_additional,
      city: draft.shippingCity || checkoutPrefill.value.city,
      state: draft.shippingState || checkoutPrefill.value.state,
      zip: draft.shippingZip || zipCode.value || checkoutPrefill.value.zip,
      country: draft.country || checkoutPrefill.value.country,
      email: draft.email || checkoutPrefill.value.email,
      phone: draft.phone || checkoutPrefill.value.phone,
    };
    saveCheckoutPrefill();
    return draft;
  } catch (error) {
    console.warn("Failed to persist Stripe checkout draft:", error);
    return null;
  }
};

const toFedExCountry = (country) => {
  const value = String(country || "")
    .trim()
    .toUpperCase();
  if (!value || value.includes("UNITED STATES") || value === "US") {
    return "US";
  }
  return value.length === 2 ? value : "US";
};

const stripePrefill = computed(() => ({
  ...checkoutPrefill.value,
  zip: zipCode.value || checkoutPrefill.value.zip || "",
}));

const makercrateBundleCount = computed(() =>
  (makercrates.value || []).reduce(
    (sum, item) => sum + (Number(item.quantity) || 1),
    0
  )
);

const parsePaymentIntentResponse = (response) => {
  const data = response?.data || {};
  const taxRaw = data.tax ?? data.tax_cents ?? data.taxCents;
  const taxCents = Number(taxRaw);

  return {
    clientSecret: data.clientSecret || data.client_secret || "",
    paymentIntentId: data.paymentIntentId || data.payment_intent_id || "",
    taxCents:
      taxRaw == null || taxRaw === "" || !Number.isFinite(taxCents)
        ? null
        : taxCents,
    error:
      response?.error ||
      response?.message ||
      (!data.clientSecret && !data.client_secret
        ? "Failed to prepare payment intent"
        : null),
  };
};

const applyPaymentIntentResponse = (response) => {
  const parsed = parsePaymentIntentResponse(response);
  if (parsed.clientSecret) {
    clientSecret.value = parsed.clientSecret;
  }
  if (parsed.paymentIntentId) {
    paymentIntentId.value = parsed.paymentIntentId;
  }
  if (parsed.taxCents != null) {
    stripeTaxCents.value = Math.max(0, parsed.taxCents);
  }
  return parsed;
};

const ensurePaymentIntent = async () => {
  if (paymentIntentId.value && clientSecret.value) {
    return {
      clientSecret: clientSecret.value,
      paymentIntentId: paymentIntentId.value,
    };
  }

  if (!cartId.value) {
    throw new Error("Cart is not ready yet. Please try again.");
  }

  const response = await stripePaymentService.createPaymentIntent(
    buildCreatePaymentIntentPayload({
      cartId: cartId.value,
      count: makercrateBundleCount.value,
      currency: "usd",
    })
  );

  const parsed = applyPaymentIntentResponse(response);
  if (!parsed.clientSecret) {
    throw new Error(parsed.error || "Failed to create payment intent");
  }

  return parsed;
};

const isMakercrateOnlyCart = computed(
  () =>
    hasMakercrates.value &&
    !(makercarts.value || []).length &&
    !(signatureMakercarts.value || []).length &&
    !(singleActivities.value || []).length
);

const canGetRates = computed(() => zipCode.value.trim().length >= 5);

const ratesAreCurrent = computed(
  () =>
    zipCode.value.trim() === fetchedZipCode.value &&
    isResidential.value === fetchedResidential.value &&
    shippingOptions.value.length > 0
);

const selectedShipping = computed(() =>
  shippingOptions.value.find((option) => option.id === selectedShippingId.value)
);

const canContinuePayment = computed(
  () =>
    Boolean(clientSecret.value) &&
    !isLoadingRates.value &&
    !isPreparingPayment.value &&
    ratesAreCurrent.value &&
    Boolean(selectedShipping.value)
);

const stripeExpressShippingRates = computed(() => {
  const option = selectedShipping.value;
  if (!option) {
    return [];
  }

  return [
    {
      id: String(option.id || option.serviceType || "fedex"),
      amount: Math.max(0, Math.round(Number(option.price || 0) * 100)),
      displayName: option.name || "FedEx",
    },
  ];
});

const updatePaymentIntentWithDetails = async (
  details = {},
  { updateAddress = false } = {}
) => {
  const created = await ensurePaymentIntent();
  const prefill = checkoutPrefill.value;
  const updateMethod = updateAddress
    ? "updatePaymentIntentAddress"
    : "updatePaymentIntent";
  const response = await stripePaymentService[updateMethod](
    buildUpdatePaymentIntentPayload({
      email: details.email || prefill.email,
      phone: details.phone || prefill.phone || "",
      country: details.country || prefill.country || "United States",
      customerName:
        details.customerName ||
        prefill.attention_to ||
        prefill.organization_name ||
        "",
      paymentIntentId: created.paymentIntentId || paymentIntentId.value,
      shippingPrice: selectedShipping.value?.price || 0,
      shippingCarrier: "FedEx",
      shippingService: selectedShipping.value?.serviceType,
      shippingAddress: details.shippingAddress ?? prefill.address ?? "",
      shippingAddressAdditional:
        details.shippingAddressAdditional ?? prefill.address_additional ?? "",
      shippingCity: details.shippingCity ?? prefill.city ?? "",
      shippingState: details.shippingState ?? prefill.state ?? "",
      shippingZip: details.shippingZip || zipCode.value || prefill.zip || "",
      shippingCountry:
        details.shippingCountry ||
        details.country ||
        prefill.country ||
        "United States",
    })
  );

  const parsed = applyPaymentIntentResponse(response);
  if (!clientSecret.value) {
    throw new Error(parsed.error || "Failed to update payment intent");
  }
  return parsed;
};

const preparePaymentFromStripe = async (details) => {
  if (!selectedShipping.value || !ratesAreCurrent.value) {
    return {
      success: false,
      error: "Choose a FedEx delivery option before paying.",
    };
  }

  try {
    await updatePaymentIntentWithDetails(details);
    return { success: true };
  } catch (error) {
    console.error("Failed to update payment intent from Stripe:", error);
    const message =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to update payment details. Please try again.";
    notify?.error(message);
    return { success: false, error: message };
  }
};

const clearTaxAndShipping = () => {
  shippingUpdateId.value += 1;
  shippingOptions.value = [];
  selectedShippingId.value = "";
  fetchedZipCode.value = "";
  zipNotFound.value = false;
  shippingRatesUnavailable.value = false;
  stripeTaxCents.value = 0;
};

const pricingLineItems = computed(() => {
  const items = makercrates.value || [];
  if (items.length === 0) {
    return [];
  }

  return items.map((item) => {
    const quantity = item.quantity || 1;
    return {
      id: item.id,
      name: item.name || "Makercrate",
      quantity,
      // API final_price is already the line subtotal for the current qty
      lineTotal: item.subtotal || 0,
    };
  });
});

const productSubtotal = computed(() => {
  const apiTotal = total.value?.makercratePrice || 0;
  if (apiTotal > 0) return apiTotal;
  return pricingLineItems.value.reduce((sum, item) => sum + item.lineTotal, 0);
});

const shippingCost = computed(() => selectedShipping.value?.price || 0);

const taxAmount = computed(() => stripeTaxCents.value / 100);

const grandTotal = computed(
  () => productSubtotal.value + shippingCost.value + taxAmount.value
);

const handleBack = () => {
  router.push({ name: "MakerCartCartPage" });
};

const markRatesError = (message) => {
  clearTaxAndShipping();
  zipNotFound.value = message === "State code not found.";
  shippingRatesUnavailable.value = !zipNotFound.value;
};

const updatePaymentIntentFromShipping = async () => {
  if (!selectedShipping.value || !ratesAreCurrent.value) return;

  const requestId = ++shippingUpdateId.value;
  const optionId = selectedShippingId.value;

  try {
    isPreparingPayment.value = true;
    const draft = await persistStripeDraft();
    if (
      requestId !== shippingUpdateId.value ||
      optionId !== selectedShippingId.value
    ) {
      return;
    }

    await updatePaymentIntentWithDetails(draft || {}, { updateAddress: true });
  } catch (error) {
    if (requestId !== shippingUpdateId.value) return;
    console.error("Failed to update payment intent from shipping:", error);
    notify?.error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update shipping. Please try again."
    );
  } finally {
    if (requestId === shippingUpdateId.value) {
      isPreparingPayment.value = false;
    }
  }
};

const selectShippingOption = (id) => {
  if (selectedShippingId.value === id) return;
  selectedShippingId.value = id;
  updatePaymentIntentFromShipping();
};

const handleGetRates = async ({ force = false } = {}) => {
  if (!canGetRates.value) {
    return [];
  }
  if (!force && isLoadingRates.value) {
    return shippingOptions.value;
  }

  if (!cartId.value) {
    if (!isLoadingCart.value) {
      notify?.error("Cart is not ready yet. Please try again.");
    }
    return [];
  }

  const postalCode = zipCode.value.trim();
  const residential = isResidential.value;
  const requestId = ++ratesRequestId.value;
  const previousServiceType = selectedShipping.value?.serviceType || "";

  try {
    isLoadingRates.value = true;
    zipNotFound.value = false;
    shippingRatesUnavailable.value = false;

    const validateResponse = await fedexService.validatePostalCode({
      postal_code: postalCode,
    });

    if (requestId !== ratesRequestId.value) return [];

    const isValid =
      validateResponse?.success !== false &&
      validateResponse?.data?.valid !== false;

    if (!isValid) {
      markRatesError(validateResponse?.message);
      return [];
    }

    const cleanedPostalCode =
      validateResponse?.data?.output?.cleanedPostalCode || postalCode;
    const countryCode =
      validateResponse?.data?.output?.countryCode ||
      stripeCountryCode.value ||
      "US";

    const response = await fedexService.getFedExRates({
      cart_id: cartId.value,
      postal_code: cleanedPostalCode,
      country: countryCode,
      residenial: residential,
    });

    if (requestId !== ratesRequestId.value) return [];

    if (response?.success === false) {
      markRatesError(response?.message);
      return [];
    }

    const mappedOptions = fedexService.mapFedExRatesToShippingOptions(
      response?.data || []
    );

    if (!mappedOptions.length) {
      markRatesError();
      return [];
    }

    shippingOptions.value = mappedOptions;
    fetchedZipCode.value = cleanedPostalCode;
    fetchedResidential.value = residential;
    zipNotFound.value = false;
    shippingRatesUnavailable.value = false;

    const matchedOption = mappedOptions.find(
      (option) => option.serviceType === previousServiceType
    );
    selectedShippingId.value = matchedOption?.id || mappedOptions[0].id;
    if (zipCode.value.trim() !== cleanedPostalCode) {
      zipCode.value = cleanedPostalCode;
    }
    updatePaymentIntentFromShipping();
    return mappedOptions;
  } catch (error) {
    if (requestId !== ratesRequestId.value) return [];

    console.error("[FedEx] failed", error);

    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "";

    markRatesError(message);
    return [];
  } finally {
    if (requestId === ratesRequestId.value) {
      isLoadingRates.value = false;
    }
  }
};

const clearRatesDebounce = () => {
  if (ratesDebounceTimer) {
    clearTimeout(ratesDebounceTimer);
    ratesDebounceTimer = null;
  }
};

const scheduleRatesRefresh = () => {
  clearRatesDebounce();
  ratesDebounceTimer = setTimeout(() => {
    ratesDebounceTimer = null;
    if (!canGetRates.value) return;
    if (
      zipCode.value.trim() === fetchedZipCode.value &&
      isResidential.value === fetchedResidential.value
    ) {
      isLoadingRates.value = false;
      return;
    }
    handleGetRates({ force: true });
  }, 600);
};

const handleStripeShippingChange = (details = {}) => {
  if (details.country) {
    stripeCountryCode.value = toFedExCountry(details.country);
  }
  const nextZip = String(details.postalCode || "").trim();
  if (!nextZip && !hasStripeZipEvent) {
    return;
  }
  hasStripeZipEvent = true;
  zipCode.value = nextZip;
};

const prepareShippingRatesFromStripe = async (details = {}) => {
  if (details.country) {
    stripeCountryCode.value = toFedExCountry(details.country);
  }
  if (details.postalCode) {
    zipCode.value = String(details.postalCode).trim();
  }
  const options = await handleGetRates({ force: true });
  if (!options.length) {
    return [];
  }
  return stripeExpressShippingRates.value;
};

watch([zipCode, isResidential], () => {
  if (step.value !== "payment") return;
  if (isLoadingCart.value) return;

  const trimmedZip = zipCode.value.trim();

  if (
    trimmedZip === fetchedZipCode.value &&
    isResidential.value === fetchedResidential.value &&
    shippingOptions.value.length > 0
  ) {
    clearRatesDebounce();
    return;
  }

  ratesRequestId.value += 1;
  clearRatesDebounce();

  if (trimmedZip.length < 5) {
    isLoadingRates.value = false;
    clearTaxAndShipping();
    return;
  }

  isLoadingRates.value = true;
  scheduleRatesRefresh();
});

onBeforeUnmount(() => {
  clearRatesDebounce();
  shippingUpdateId.value += 1;
  ratesRequestId.value += 1;
});

const handleContinuePayment = async () => {
  if (!canContinuePayment.value) {
    notify?.error("Choose a FedEx delivery option before paying.");
    return;
  }

  if (!stripeFormRef.value) {
    notify?.plain("Stripe checkout is not ready yet");
    return;
  }

  try {
    isValidatingStripe.value = true;
    const result = await stripeFormRef.value.confirmPayment();
    if (!result.success) {
      notify?.error(result.error || "Payment failed. Please try again.");
      return;
    }

    handlePaymentSuccess(result.paymentIntent);
  } catch (error) {
    console.error("Stripe payment failed:", error);
    notify?.error(
      error?.message || "Failed to confirm Stripe payment. Please try again."
    );
  } finally {
    isValidatingStripe.value = false;
  }
};

const handlePaymentSuccess = async () => {
  try {
    sessionStorage.removeItem(CHECKOUT_PREFILL_KEY);
  } catch (error) {
    console.warn("Failed to clear checkout prefill data:", error);
  }

  // Clear purchased makercrates from shared cart state immediately so
  // catalog/cart UI don't keep showing stale items until a hard refresh.
  makercrates.value = [];
  if (total.value) {
    total.value.makercratePrice = 0;
  }

  step.value = "success";
  window.scrollTo({ top: 0, behavior: "smooth" });

  try {
    await syncCartWithAPI();
  } catch (error) {
    console.warn("Failed to refresh cart after payment success:", error);
  }
};

const goToMakerCatalog = () => {
  router.push({ name: "MakerCartPage" });
};

const goToMakercrateResources = () => {
  router.push({ name: "MakerCartPage", query: { tab: "makercrate" } });
};

onMounted(async () => {
  try {
    isLoadingCart.value = true;
    loadCheckoutPrefill();
    await syncCartWithAPI();
    if (!isMakercrateOnlyCart.value) {
      notify?.plain("Instant checkout is available for Makercrate-only carts");
      router.replace({ name: "MakerCartCartPage" });
      return;
    }

    try {
      await ensurePaymentIntent();
    } catch (error) {
      console.warn("PaymentIntent will be created later:", error);
    }
  } catch (error) {
    console.error("Failed to load cart for instant checkout:", error);
    notify?.error("Failed to load cart");
    router.replace({ name: "MakerCartCartPage" });
  } finally {
    isLoadingCart.value = false;
  }

  if (isMakercrateOnlyCart.value && canGetRates.value) {
    handleGetRates({ force: true });
  }
});
</script>

<style scoped>
@import "@/assets/css/colors.css";

.instant-checkout-page {
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  min-height: 100vh;
  background: var(--white);
  border-top: 1px solid #d8dae5;
}

.checkout-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.checkout-progress__section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.checkout-progress__section--link {
  text-decoration: none;
}

.checkout-progress__section--link:hover .checkout-progress__label {
  text-decoration: underline;
}

.checkout-progress__section--link:focus-visible {
  outline: 2px solid var(--primary-blue);
  outline-offset: 4px;
}

.checkout-progress__icon {
  width: 40px;
  height: 40px;
  border-radius: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
}

.checkout-progress__icon--done {
  background: var(--primary-green);
}

.checkout-progress__icon--current {
  background: var(--white);
  border: 3px solid var(--primary-green);
}

.checkout-progress__icon--pending {
  background: var(--white);
  border: 3px solid #98a2b3;
}

.checkout-progress__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--primary-green);
}

.checkout-progress__dot--pending {
  background: #98a2b3;
}

.checkout-progress__label {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  color: var(--neutral-700);
  white-space: nowrap;
}

.checkout-progress__label--active {
  color: var(--black-body-text);
}

.checkout-progress__line {
  flex: 1;
  height: 5px;
  border-radius: 10px;
  background: #e5e7eb;
  margin: 0 16px;
}

.checkout-progress__line--active {
  background: var(--primary-green);
}

.checkout-divider {
  width: 100%;
  height: 1px;
  background: #e5e7eb;
}

.checkout-back {
  display: flex;
  align-items: center;
  gap: 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  font-family: "League Spartan", sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  color: var(--primary-blue);
}

.checkout-back__icon {
  width: 30px;
  height: 30px;
}

.checkout-content {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
}

.checkout-main {
  flex: 1;
  min-width: 0;
  position: relative;
  background: var(--white);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow:
    0 1px 2px 0 rgba(0, 0, 0, 0.3),
    0 2px 6px 2px rgba(0, 0, 0, 0.15);
}

.checkout-payment {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.checkout-main__header {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.checkout-main__title {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 36px;
  color: var(--black-body-text);
}

.checkout-main__subtitle {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--black-body-text);
}

.checkout-main__divider {
  width: 100%;
  border-top: 1px dashed #d0d5dd;
}

.residential-check {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  box-sizing: border-box;
}

.residential-check__control {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.residential-check__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
}

.residential-check__box {
  width: 24px;
  height: 24px;
  border: 1px solid var(--white-inactive);
  border-radius: 4px;
  background: var(--white);
  box-sizing: border-box;
  position: relative;
}

.residential-check__input:focus-visible + .residential-check__box {
  outline: 2px solid var(--primary-blue);
  outline-offset: 2px;
}

.residential-check__input:checked + .residential-check__box {
  background: var(--primary-blue);
  border-color: var(--primary-blue);
}

.residential-check__box::after {
  content: "";
  position: absolute;
  display: none;
  left: 7px;
  top: 3px;
  width: 6px;
  height: 11px;
  border: solid var(--white);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.residential-check__input:checked + .residential-check__box::after {
  display: block;
}

.residential-check__copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  min-width: 0;
  flex: 1;
}

.residential-check__title {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--black-body-text);
}

.residential-check__subtitle {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xs);
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: var(--black-body-text);
}

.residential-check--disabled {
  opacity: 0.7;
  pointer-events: none;
  cursor: not-allowed;
}

.payment-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 34px;
  width: 100%;
  max-width: 866px;
  margin: 0 auto;
  padding: 0 0 48px;
  box-sizing: border-box;
}

.payment-success__hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 776px;
}

.payment-success__illustration {
  width: 500px;
  max-width: 100%;
  height: auto;
  display: block;
}

.payment-success__heading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  text-align: center;
}

.payment-success__title {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  color: var(--primary-pink);
}

.payment-success__subtitle {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  color: var(--black-body-text);
}

.payment-success__next {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  box-sizing: border-box;
}

.payment-success__next-title {
  margin: 0;
  width: 100%;
  font-family: "League Spartan", sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 36px;
  text-align: center;
  color: var(--black-body-text);
}

.payment-success__next-divider {
  width: 100%;
  border-top: 1px dashed #d0d5dd;
}

.payment-success__next-body {
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: 100%;
  text-align: center;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  color: var(--black-body-text);
}

.payment-success__next-body p {
  margin: 0;
}

.payment-success__link {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 500;
  line-height: 28px;
  color: var(--primary-blue);
  text-decoration: underline;
  text-underline-position: from-font;
}

.payment-success__link:hover {
  color: var(--primary-blue);
}

.zip-not-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  width: 100%;
  padding: 12px 24px;
  border-radius: 12px;
  box-sizing: border-box;
}

.zip-not-found__illustration {
  width: 344px;
  max-width: 100%;
  height: auto;
  aspect-ratio: 1 / 1;
  display: block;
  object-fit: contain;
}

.zip-not-found__copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  text-align: center;
  color: var(--black-body-text);
}

.zip-not-found__title {
  margin: 0;
  width: 100%;
  font-family: "League Spartan", sans-serif;
  font-size: 32px;
  font-weight: 500;
  line-height: 40px;
}

.zip-not-found__subtitle {
  margin: 0;
  width: 100%;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.03px;
}

.shipping-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.shipping-options__heading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "League Spartan", sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  color: var(--black-body-text);
}

.shipping-options__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 120px;
  padding: 24px 16px;
  margin: 0;
  border-radius: 16px;
  background: #f5f8fa;
  box-sizing: border-box;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  color: var(--black-body-text);
  text-align: center;
}

.shipping-options__skeletons {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.shipping-option--skeleton {
  cursor: default;
  pointer-events: none;
  background: #f5f8fa;
  box-shadow: none;
}

.shipping-skeleton__radio {
  border-color: #d0d5dd;
  background: #e4e7ec;
}

.shipping-skeleton__line {
  display: block;
  border-radius: 6px;
  background: linear-gradient(90deg, #e4e7ec 0%, #f2f4f7 50%, #e4e7ec 100%);
  background-size: 200% 100%;
  animation: shipping-skeleton-shimmer 1.4s ease-in-out infinite;
}

.shipping-skeleton__line--title {
  width: 55%;
  height: 20px;
}

.shipping-skeleton__line--meta {
  width: 35%;
  height: 16px;
}

.shipping-skeleton__line--eta {
  width: 70%;
  height: 16px;
}

.shipping-skeleton__line--price {
  width: 72px;
  height: 22px;
  flex-shrink: 0;
  align-self: center;
}

@keyframes shipping-skeleton-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.shipping-option {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: none;
  border-radius: 24px;
  background: var(--white);
  box-shadow:
    0 1px 2px 0 rgba(0, 0, 0, 0.3),
    0 2px 6px 2px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  text-align: left;
}

.shipping-option.selected,
.shipping-option--summary {
  background: #e6ecf0;
}

.shipping-option__radio {
  width: 24px;
  height: 24px;
  margin: 12px;
  border: 2px solid #6c737f;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.shipping-option__radio.checked {
  border-color: var(--primary-blue);
}

.shipping-option__radio.checked span {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--primary-blue);
}

.shipping-option__body {
  display: flex;
  flex: 1;
  gap: 12px;
  align-items: center;
  min-width: 0;
}

.shipping-option__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.shipping-option__copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.shipping-option__name {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--font-size-headline-medium);
  font-weight: 700;
  line-height: var(--line-height-headline-medium);
  color: var(--primary-blue);
}

.shipping-option__meta {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--neutral-700);
}

.shipping-option__eta {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.024px;
  color: var(--primary-green);
}

.shipping-option__price {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--font-size-headline-small);
  font-weight: 700;
  line-height: var(--line-height-headline-small);
  color: var(--black-body-text);
  white-space: nowrap;
}

.checkout-powered {
  margin: 0;
  text-align: center;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  color: #98a2b3;
}

.pricing-summary {
  width: 450px;
  max-width: 100%;
  flex-shrink: 0;
  background: var(--white);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow:
    0 1px 2px 0 rgba(0, 0, 0, 0.3),
    0 2px 6px 2px rgba(0, 0, 0, 0.15);
}

.pricing-summary__title {
  margin: 0;
  width: 100%;
  font-family: "League Spartan", sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 36px;
  color: var(--black-body-text);
}

.pricing-summary__body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}

.pricing-summary__divider {
  width: 100%;
  height: 0;
  border: none;
  border-top: 1px solid #dfe2e7;
}

.pricing-summary__divider--dashed {
  border-top: 1px dashed #d0d5dd;
}

.pricing-summary__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  color: var(--black-body-text);
  white-space: nowrap;
}

.pricing-summary__subtotal {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  width: 100%;
}

.pricing-summary__note {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  color: var(--neutral-500);
}

.pricing-summary__item-label {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
}

.pricing-summary__item-name {
  font-size: var(--fs-large);
  line-height: 28px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pricing-summary__item-qty {
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  flex-shrink: 0;
}

.pricing-summary__value {
  margin: 0;
  flex-shrink: 0;
}

.pricing-summary__row--total {
  align-items: center;
}

.pricing-summary__total-label {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-weight: 400;
}

.pricing-summary__total-label > span:first-child {
  font-size: var(--fs-large);
  line-height: 28px;
}

.pricing-summary__currency {
  font-size: var(--fs-big);
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--primary-purple);
}

.pricing-summary__total-value {
  margin: 0;
  font-size: var(--fs-large);
  font-weight: 700;
  line-height: 28px;
}

@media (max-width: 1280px) {
  .checkout-content {
    flex-direction: column;
    align-items: stretch;
  }

  .checkout-main,
  .pricing-summary {
    width: 100%;
    max-width: none;
    align-self: stretch;
    box-sizing: border-box;
  }
}

@media (max-width: 768px) {
  .instant-checkout-page {
    padding: 16px;
    gap: 16px;
  }

  .checkout-progress__label {
    font-size: var(--fs-small);
    line-height: 20px;
  }

  .checkout-progress__icon {
    width: 24px;
    height: 24px;
  }

  .checkout-progress__dot {
    width: 6px;
    height: 6px;
  }

  .checkout-progress__line {
    margin: 0;
  }

  .checkout-back {
    font-size: var(--fs-large);
    line-height: 28px;
    gap: 8px;
  }

  .checkout-back__icon {
    width: 24px;
    height: 24px;
  }

  .checkout-main__title {
    font-size: var(--fs-xl);
    line-height: 32px;
  }

  .zip-not-found {
    padding: 8px 0;
    gap: 16px;
  }

  .zip-not-found__illustration {
    width: 240px;
  }

  .zip-not-found__title {
    font-size: var(--fs-xl);
    line-height: 32px;
  }

  .payment-success {
    gap: 24px;
  }

  .payment-success__illustration {
    width: 280px;
  }

  .payment-success__title {
    font-size: var(--fs-xl);
    line-height: 32px;
  }

  .payment-success__next-title {
    font-size: var(--fs-xl);
    line-height: 32px;
  }

  .payment-success__next-body,
  .payment-success__link {
    font-size: var(--fs-medium);
    line-height: 24px;
  }
}
</style>
